import { FC } from "react"
import { Alert, Box, Stack, TextField, Typography } from "@mui/material"
import { UserbotCatalog } from "../../interfaces"
import { ACTION_HINTS } from "../../constants/actionHints"
import { FieldSpecForm } from "../fields/fieldSpecForm"
import { isValidJson, parseJsonValue } from "../common/jsonUtils"
import { coerceFieldValue } from "../fields/fieldSpecCoerce"
import { validateHistoryConsent } from "@/core/utils/historyConsent"
import { paramsToValues, valuesToParams } from "./actionParams"

interface IProps {
  descriptor?: UserbotCatalog.ActionDescriptor
  values: Record<string, unknown>
  /** Raw and typed values belong to one node and must be replaced atomically. */
  onChange: (values: Record<string, unknown>, rawParamsJson: string) => void
  rawParamsJson: string
  advanced: boolean
  errors?: Record<string, string>
  headHints?: string[]
}

/** Динамическая форма параметров действия по каталогу (FieldSpec) + JSON-fallback. */
export const ParamsForm: FC<IProps> = ({
  descriptor,
  values,
  onChange,
  rawParamsJson,
  advanced,
  errors = {},
  headHints,
}) => {
  const hints = descriptor ? (ACTION_HINTS[descriptor.name] ?? []) : []
  const hasTyped = !!descriptor
  const isExport = descriptor?.name === "telegram.exportHistory"
  // Consent validation must not parse unrelated JSON-field drafts.
  const typedConsent = isExport
    ? Object.fromEntries(
        descriptor.params
          .filter(
            (spec) =>
              spec.type === "boolean" &&
              (spec.key === "downloadMessages" || spec.key === "analyze"),
          )
          .map((spec) => [spec.key, coerceFieldValue(spec, values[spec.key])]),
      )
    : {}
  const previewParams: Record<string, unknown> = {}
  let previewHasInvalidFields = false
  if (descriptor && advanced && !rawParamsJson.trim()) {
    for (const spec of descriptor.params) {
      const raw = values[spec.key]
      if (raw === undefined) continue
      try {
        previewParams[spec.key] = coerceFieldValue(spec, raw)
      } catch {
        // A preview is not serialization: retain the draft and report the error.
        previewParams[spec.key] = raw
        previewHasInvalidFields = true
      }
    }
    if (!previewHasInvalidFields)
      Object.assign(previewParams, valuesToParams(descriptor.params, values))
  }
  // Draft JSON may be incomplete during ordinary typing; keep it verbatim.
  const parsed =
    isExport && isValidJson(rawParamsJson)
      ? parseJsonValue<Record<string, unknown>>(rawParamsJson)
      : undefined
  const exportParams = rawParamsJson.trim() ? parsed : typedConsent
  const consentErrors = isExport
    ? validateHistoryConsent(
        exportParams?.downloadMessages,
        exportParams?.analyze,
      )
    : []
  const consentNotice = consentErrors.map((message) => (
    <Alert key={message} severity="error">
      {message}
    </Alert>
  ))

  const changeRaw = (raw: string) => {
    const params = isValidJson(raw)
      ? parseJsonValue<Record<string, unknown>>(raw)
      : undefined
    let nextValues = values
    if (
      descriptor &&
      params &&
      typeof params === "object" &&
      !Array.isArray(params)
    ) {
      // Reflect raw consent revocation in the typed switches before further edits.
      nextValues = paramsToValues(descriptor.params, params)
    }
    onChange(nextValues, raw)
  }

  const renderHintsBlock = () =>
    hints.length > 0 && (
      <Alert severity="info" sx={{ mb: 1.5 }}>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {hints.map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
      </Alert>
    )

  // Сырой JSON: если действие не описано каталогом ИЛИ включён advanced-режим.
  if (
    !hasTyped ||
    advanced ||
    (rawParamsJson.trim() !== "" && !isValidJson(rawParamsJson))
  ) {
    const invalid = rawParamsJson.trim() !== "" && !isValidJson(rawParamsJson)
    const helper = !hasTyped
      ? "Параметры (JSON) — действие не описано каталогом"
      : "Параметры (JSON) — расширенный режим"
    return (
      <Stack spacing={1}>
        {renderHintsBlock()}
        {consentNotice}
        {previewHasInvalidFields && (
          <Alert severity="error">
            Некорректный JSON в полях параметров. Исправьте черновик в форме.
          </Alert>
        )}
        {!descriptor && (
          <Typography variant="body2" color="text.secondary">
            Неизвестное действие — задайте параметры вручную (JSON).
          </Typography>
        )}
        <TextField
          label="Параметры (JSON)"
          value={
            descriptor && !rawParamsJson.trim()
              ? JSON.stringify(
                  {
                    ...previewParams,
                    ...(isExport
                      ? {
                          downloadMessages:
                            typedConsent.downloadMessages ?? false,
                          analyze: typedConsent.analyze ?? false,
                        }
                      : {}),
                  },
                  null,
                  2,
                )
              : rawParamsJson
          }
          onChange={(e) => changeRaw(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          error={invalid}
          helperText={invalid ? "Некорректный JSON" : helper}
          InputProps={{ style: { fontFamily: "monospace" } }}
        />
      </Stack>
    )
  }

  if (descriptor && descriptor.params.length === 0) {
    return (
      <Box>
        {renderHintsBlock()}
        <Typography variant="body2" color="text.secondary">
          Действие не требует параметров.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {renderHintsBlock()}
      {consentNotice}
      <FieldSpecForm
        specs={descriptor!.params}
        values={values}
        onChange={(next) => {
          try {
            const params = valuesToParams(descriptor!.params, next)
            onChange(next, isExport ? "" : JSON.stringify(params, null, 2))
          } catch {
            // Partial nested JSON is a typed draft, not an exception or stale raw body.
            onChange(next, "")
          }
        }}
        errors={errors}
        headHints={headHints}
      />
    </Box>
  )
}
